import { useEffect, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, Loader2, RefreshCw, Settings2, XCircle, Trash2, Sparkles, CalendarIcon, Zap, Clock, Archive } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format, parse, isValid } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ManualCategorizeDialog } from "../components/ManualCategorizeDialog";
import FileMakerAuditPanel from "../components/FileMakerAuditPanel";
import SampleWarehousesPanel from "../components/SampleWarehousesPanel";

// Serialize/deserialize between the MM/DD/YYYY string stored in settings and
// the Date object react-day-picker expects. Empty string => null (no date).
function parseFMDate(s) {
  if (!s) return null;
  const d = parse(s, "MM/dd/yyyy", new Date());
  return isValid(d) ? d : null;
}
function formatFMDate(d) {
  return d ? format(d, "MM/dd/yyyy") : "";
}

function DatePickerField({ value, onChange, testId, placeholder = "Pick a date" }) {
  const parsed = parseFMDate(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testId}
          className="mt-1.5 w-full justify-start text-left font-mono text-sm rounded-lg border-gray-300 hover:border-brand-500 hover:bg-white"
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
          {parsed ? formatFMDate(parsed) : <span className="text-gray-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={(d) => onChange(formatFMDate(d))}
          initialFocus
          captionLayout="dropdown-buttons"
          fromYear={2020}
          toYear={2035}
        />
        <div className="flex justify-end p-2 border-t border-gray-100">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            data-testid={`${testId}-clear`}
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Settings() {
  const [sysSettings, setSysSettings] = useState({
    sea_freight_per_container: "", usd_to_rmb_rate: "", usd_to_cad_rate: "",
    quotation_rate: "", profit_margin_rate: "", default_tax_rebate_rate: "",
    sync_date_from: "", sync_date_to: "",
    sync_tag_filter: "",
    photo_auto_refresh_days: "",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [fmStatus, setFmStatus] = useState(null); // { connected, message }
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [syncState, setSyncState] = useState(null); // { status, message, result }
  const [syncHistory, setSyncHistory] = useState([]);
  const pollRef = useRef(null);
  // iter 150: monotonically-incrementing generation. Any status response
  // returning from a poll started before this counter's current value is
  // considered stale and ignored - fixes the race where the mount-time
  // poll ("idle") resolves AFTER a fresh POST /sync ("running") and
  // clobbers the "running" state, making the Stop button flash and vanish.
  const syncGenRef = useRef(0);

  // Category rules (self-learning keyword → category mappings)
  const [categoryRules, setCategoryRules] = useState([]);
  const [newRuleKeyword, setNewRuleKeyword] = useState("");
  const [newRuleCategory, setNewRuleCategory] = useState("bedding");
  const [savingRule, setSavingRule] = useState(false);

  // iter 158: photo backfill skip-tracker. `pending` = will retry next run;
  // `failed_current` = errored last run and not yet skipped; `skipped` =
  // user has flagged as broken-in-FM.
  const [photoStats, setPhotoStats] = useState({ pending: 0, failed_current: 0, skipped: 0 });
  const [photoActionLoading, setPhotoActionLoading] = useState(false);
  // iter 164: full health breakdown so users can see exactly why photos
  // aren't rendering (no source in FM / skipped by user / pending / etc).
  const [photoHealth, setPhotoHealth] = useState(null);
  // iter 158b: grouped failure reasons dialog
  const [failuresOpen, setFailuresOpen] = useState(false);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [failuresGroups, setFailuresGroups] = useState([]);

  const openFailuresDialog = async () => {
    setFailuresOpen(true);
    setFailuresLoading(true);
    setFailuresGroups([]);
    try {
      const { data } = await api.get("/filemaker/photo-backfill/failures");
      setFailuresGroups(data.groups || []);
    } catch (e) {
      toast.error("Failed to load failure reasons");
    } finally {
      setFailuresLoading(false);
    }
  };

  const loadPhotoStats = async () => {
    try {
      const [{ data: stats }, { data: health }] = await Promise.all([
        api.get("/filemaker/photo-backfill/stats"),
        api.get("/filemaker/photo-backfill/health"),
      ]);
      setPhotoStats(stats);
      setPhotoHealth(health);
    } catch (e) {
      // non-critical
    }
  };

  const handleMarkFailedAsSkip = async () => {
    if (!window.confirm(`Mark ${photoStats.failed_current} failed photo(s) as skipped? They won't be retried until you click "Retry skipped" (after fixing them in FileMaker).`)) return;
    setPhotoActionLoading(true);
    try {
      const { data } = await api.post("/filemaker/photo-backfill/mark-failed-as-skip");
      toast.success(`Marked ${data.marked} photo(s) as skipped`);
      setPhotoStats({ pending: data.pending, failed_current: data.failed_current, skipped: data.skipped });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to mark as skip");
    } finally {
      setPhotoActionLoading(false);
    }
  };

  const handleClearSkip = async () => {
    if (!window.confirm(`Clear skip flag on ${photoStats.skipped} photo(s)? They'll be retried on the next Photo Backfill run.`)) return;
    setPhotoActionLoading(true);
    try {
      const { data } = await api.post("/filemaker/photo-backfill/clear-skip");
      toast.success(`Cleared skip flag on ${data.cleared} photo(s)`);
      setPhotoStats({ pending: data.pending, failed_current: data.failed_current, skipped: data.skipped });
      loadPhotoStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to clear skip flags");
    } finally {
      setPhotoActionLoading(false);
    }
  };

  // iter 165: rescan FM for products whose DB has no photo_container at all.
  // Useful when users add photos to FM AFTER the products were synced (they
  // fall outside the current sync date window, so normal sync won't touch
  // them again).
  const handleRefreshPhotoSources = async () => {
    const total = photoHealth?.no_source_in_fm || 0;
    if (!window.confirm(`Re-query FileMaker for ${total} product(s) currently missing photo sources? This ignores the sync date filter and pulls whatever FM has now.`)) return;
    setPhotoActionLoading(true);
    try {
      const { data } = await api.post("/filemaker/photo-backfill/refresh-sources?limit=1000");
      toast.success(`Scanned ${data.scanned}, refreshed ${data.updated} source URL(s), ${data.remaining_after} still no-source in FM`);
      loadPhotoStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setPhotoActionLoading(false);
    }
  };

  // iter 166: per-product inspect + force-refresh diagnostic. Lets user
  // pinpoint "why does THIS specific product not have a photo" by comparing
  // DB state vs live FM lookup, then force-refresh to trigger a fresh
  // download.
  const [inspectSerial, setInspectSerial] = useState("");
  const [inspectResult, setInspectResult] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);

  const handleInspect = async () => {
    const s = (inspectSerial || "").trim();
    if (!s) { toast.error("Enter a serial number first"); return; }
    setInspectLoading(true);
    setInspectResult(null);
    try {
      const { data } = await api.get(`/filemaker/photo-backfill/inspect/${encodeURIComponent(s)}`);
      setInspectResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Inspect failed");
    } finally {
      setInspectLoading(false);
    }
  };

  const handleForceRefresh = async () => {
    const s = (inspectSerial || "").trim();
    if (!s) return;
    setInspectLoading(true);
    try {
      const { data } = await api.post(`/filemaker/photo-backfill/force-refresh/${encodeURIComponent(s)}`);
      toast.success(data.message);
      // Re-inspect to show fresh state
      await handleInspect();
      loadPhotoStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Force refresh failed");
    } finally {
      setInspectLoading(false);
    }
  };

  // iter 167: Path B bulk fix — force refresh photos for products whose
  // date_enter falls in a range (typically where R2 objects were wiped).
  const [rangeFrom, setRangeFrom] = useState("2026-01-01");
  const [rangeTo, setRangeTo] = useState("2026-08-01");
  const [rangeDryRun, setRangeDryRun] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);

  const handleForceRefreshRange = async () => {
    if (!rangeFrom || !rangeTo) { toast.error("Set both dates"); return; }
    if (!rangeDryRun && !window.confirm(`Force refresh photos for all products with date_enter between ${rangeFrom} and ${rangeTo}? Their photo_url will be cleared and re-downloaded from FileMaker.`)) return;
    setRangeLoading(true);
    try {
      const { data } = await api.post(`/filemaker/photo-backfill/force-refresh-date-range?date_from=${rangeFrom}&date_to=${rangeTo}&dry_run=${rangeDryRun}`);
      if (data.dry_run) {
        toast.info(`DRY RUN: ${data.matched} product(s) would be refreshed. Uncheck 'Dry Run' to actually run.`);
      } else {
        toast.success(`Refreshed ${data.refreshed} of ${data.matched} (${data.fm_missing} had no FM source). Now click Backfill Photos to download.`);
        loadPhotoStats();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Range refresh failed");
    } finally {
      setRangeLoading(false);
    }
  };

  const loadCategoryRules = async () => {
    try {
      const { data } = await api.get("/category-rules");
      setCategoryRules(data.items || []);
    } catch (e) {
      // non-critical
    }
  };

  const handleAddRule = async () => {
    const kw = newRuleKeyword.trim().toLowerCase();
    if (kw.length < 2) { toast.error("Keyword must be at least 2 characters"); return; }
    setSavingRule(true);
    try {
      const { data } = await api.post("/category-rules", { keyword: kw, category: newRuleCategory });
      toast.success(`Rule saved · applied to ${data.applied_to_existing} existing product(s)`);
      setNewRuleKeyword("");
      loadCategoryRules();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save rule");
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.keyword}" → ${rule.category}?`)) return;
    try {
      await api.delete(`/category-rules/${rule.id}`);
      toast.success("Rule deleted");
      loadCategoryRules();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setSysSettings({
        sea_freight_per_container: data.sea_freight_per_container ?? "",
        usd_to_rmb_rate: data.usd_to_rmb_rate ?? "",
        usd_to_cad_rate: data.usd_to_cad_rate ?? "",
        quotation_rate: data.quotation_rate ?? "",
        profit_margin_rate: data.profit_margin_rate ?? "",
        default_tax_rebate_rate: data.default_tax_rebate_rate ?? "",
        sync_date_from: data.sync_date_from ?? "",
        sync_date_to: data.sync_date_to ?? "",
        sync_tag_filter: data.sync_tag_filter ?? "",
        photo_auto_refresh_days: data.photo_auto_refresh_days ?? 7,
      });
    }).catch(() => {
      // non-critical
    });
    checkFmStatus();
    pollSyncStatus();
    loadCategoryRules();
    loadSyncHistory();
    loadPhotoStats();
    return () => { clearTimeout(pollRef.current); };
  }, []);

  const checkFmStatus = async () => {
    setCheckingStatus(true);
    try {
      const { data } = await api.get("/filemaker/status");
      setFmStatus(data);
    } catch (e) {
      setFmStatus({ connected: false, message: "Unable to check connection status" });
    } finally {
      setCheckingStatus(false);
    }
  };

  const pollSyncStatus = async () => {
    // Capture the generation this poll belongs to. If it changes while we
    // await (because handleSync fired), drop the stale response.
    const myGen = syncGenRef.current;
    try {
      const { data } = await api.get("/filemaker/sync/status");
      if (myGen !== syncGenRef.current) return;
      const prevStatus = syncState?.status;
      const prevPhotoStatus = syncState?.photo_backfill?.status;
      setSyncState(data);
      if (
        data.status === "running" ||
        data.photo_backfill?.status === "running" ||
        data.category_classification?.status === "running"
      ) {
        pollRef.current = setTimeout(pollSyncStatus, 3000);
      } else if (prevStatus === "running" && data.status !== "running") {
        loadSyncHistory();
      }
      // iter 158: refresh photo skip/failed counts when a backfill run
      // transitions out of "running" so the mark/retry buttons update.
      if (prevPhotoStatus === "running" && data.photo_backfill?.status !== "running") {
        loadPhotoStats();
      }
    } catch (e) {
      // non-critical
    }
  };

  const handleSync = async (mode = "all") => {
    // Bump generation so any in-flight status poll is invalidated. Then
    // optimistically set "running" state to make the Stop button appear
    // immediately, and schedule the next status poll shortly after.
    syncGenRef.current += 1;
    clearTimeout(pollRef.current);
    try {
      const { data } = await api.post("/filemaker/sync", null, { params: { mode } });
      setSyncState({ ...data, status: "running" });
      const label = mode === "all" ? "Full sync" : mode === "contracts" ? "Contract sync" : "Product sync";
      toast.info(`${label} started, please wait...`);
      pollRef.current = setTimeout(pollSyncStatus, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start sync");
    }
  };

  const handleStopSync = async () => {
    try {
      const { data } = await api.post("/filemaker/sync/stop");
      toast.info(data.message || "Stop signal sent");
      pollRef.current = setTimeout(pollSyncStatus, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to stop sync");
    }
  };

  const loadSyncHistory = async () => {
    try {
      const { data } = await api.get("/filemaker/sync/history", { params: { limit: 20 } });
      setSyncHistory(data.items || []);
    } catch (e) {
      // Silent - history is a supplementary panel; toasts on every reload would be noisy.
    }
  };

  const handlePhotoBackfill = async () => {
    try {
      const { data } = await api.post("/filemaker/photo-backfill");
      if (data.status === "idle") {
        toast.info(data.message || "No photos pending");
      } else {
        toast.info(data.message || "Photo backfill started");
        pollRef.current = setTimeout(pollSyncStatus, 2000);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start photo backfill");
    }
  };

  const handleClassifyCategories = async () => {
    try {
      const { data } = await api.post("/products/classify-categories");
      toast.info(data.message || "Category classification started");
      pollRef.current = setTimeout(pollSyncStatus, 2000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start classification");
    }
  };

  // iter 163: retry only rows currently marked 'unknown' (doesn't touch
  // user-overridden categories).
  const handleRetryUnknowns = async () => {
    try {
      const { data } = await api.post("/products/classify-unknowns");
      toast.info(data.message || "Retry started");
      pollRef.current = setTimeout(pollSyncStatus, 2000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start retry");
    }
  };

  const [manualCategorizeOpen, setManualCategorizeOpen] = useState(false);
  const handlePurgeUndated = async () => {
    // iter 114: Removes products missing PM.Date_enter and orphan
    // price_entries missing 输入日期 - these are stragglers pulled in
    // by fallback paths that don't respect the sync date window.
    // Cart-referenced products go to archived_products so open quotes
    // still resolve. Confirm here since it's a destructive one-shot.
    const yes = window.confirm(
      "Purge all products / price entries that FileMaker never dated?\n\n" +
      "Products missing 'Date_enter' (product_main) and price entries missing '输入日期' (data_api) will be removed.\n" +
      "Any product currently used in a quotation will be archived (not deleted) so open quotes still work."
    );
    if (!yes) return;
    try {
      const { data } = await api.post("/filemaker/purge-undated");
      toast.success(
        `Purged: ${data.products_removed} products removed, ${data.products_archived} archived, ${data.entries_removed} orphan entries removed`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Purge failed");
    }
  };

  // iter 154: FM product_main.Date_enter backfill.
  // Two-step confirm on the first run, then auto-loop until every empty
  // date has been filled. Users click once and watch the toast update.
  const handleBackfillFmDates = async () => {
    // Show immediate feedback since the dry-run FM query takes ~12s.
    const initialToast = toast.loading("扫描 FileMaker 中,请稍候(约 12 秒)…");
    let dryRun;
    try {
      const resp = await api.post("/admin/backfill-fm-product-dates", {});
      dryRun = resp.data;
      toast.dismiss(initialToast);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "扫描失败", { id: initialToast });
      return;
    }
    try {
      if (dryRun.would_update === 0) {
        toast.info(`No records to backfill (examined ${dryRun.examined}, all skipped).`);
        return;
      }
      const yes = window.confirm(
        `🚨 回填 FM Date_enter 确认\n\n` +
        `本次扫描 ${dryRun.examined} 条空 Date_enter 的产品:\n` +
        `  • 可回填: ${dryRun.would_update} 条 (取最早合同作业日期)\n` +
        `  • 跳过 (无 data_api 关联): ${dryRun.skipped_no_data_api} 条\n` +
        `  • 跳过 (合同也无日期): ${dryRun.skipped_no_contract_date} 条\n\n` +
        `示例:\n${(dryRun.sample_plan || []).slice(0, 3).map(p => `  serial=${p.serial} → ${p.new_date}`).join("\n")}\n\n` +
        `点击"确定"后将自动循环直到全部填完 (每批 300 条,每次 ~40 秒),期间可关闭页面取消。\n\n` +
        `确定继续?`
      );
      if (!yes) return;

      // Auto-loop until would_update=0 or we hit a safety ceiling.
      const toastId = toast.loading("Starting backfill…");
      let totalUpdated = 0, totalFailed = 0, totalSkipped = 0, iter = 0;
      const MAX_ITERATIONS = 50;   // 50 * 100 = 5,000 records ceiling
      while (iter < MAX_ITERATIONS) {
        iter += 1;
        toast.loading(`Batch ${iter}: patching FileMaker…`, { id: toastId });
        // iter 154e: retry each batch up to 3x so transient 401/502 don't
        // abort the whole loop — user asked "同步几批以后会自动停止".
        let data = null;
        let lastErr = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const resp = await api.post("/admin/backfill-fm-product-dates", { confirm: true });
            data = resp.data;
            break;
          } catch (e) {
            lastErr = e;
            const status = e.response?.status;
            toast.loading(
              `Batch ${iter}: retry ${attempt}/3 after ${status || "network"} error…`,
              { id: toastId }
            );
            await new Promise((r) => setTimeout(r, 3000));  // brief backoff
          }
        }
        if (!data) {
          toast.error(
            `Batch ${iter} failed after 3 retries: ${formatApiErrorDetail(lastErr?.response?.data?.detail) || lastErr?.message || "unknown"}. Running total: ${totalUpdated}`,
            { id: toastId, duration: 10000 }
          );
          return;
        }
        totalUpdated += data.updated || 0;
        totalFailed += data.failed || 0;
        totalSkipped += (data.skipped_no_data_api || 0) + (data.skipped_no_contract_date || 0);
        toast.loading(
          `Batch ${iter} done: +${data.updated} updated, ${data.failed} failed. Running total: ${totalUpdated}`,
          { id: toastId }
        );
        if ((data.would_update || 0) === 0) break;
      }
      toast.success(
        `Backfill complete: ${totalUpdated} FM records updated across ${iter} batches (${totalFailed} failed, ${totalSkipped} skipped).`,
        { id: toastId, duration: 8000 }
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed");
    }
  };

  // iter 153: R2 orphan cleanup with two-step confirm. First call is a
  // dry-run (returns orphan count), then user confirms actual deletion.
  const handleCleanupR2 = async () => {
    if (isSyncing || isPhotoBackfilling) {
      toast.error("Wait for sync / Photo Backfill to finish before cleaning R2");
      return;
    }
    try {
      // Dry-run
      const { data: dryRun } = await api.post("/admin/cleanup-r2-orphans", {});
      if (dryRun.orphans === 0) {
        toast.info(`R2 clean already: ${dryRun.in_use} in use, no orphans`);
        return;
      }
      const yes = window.confirm(
        `🚨 R2 Cleanup 确认\n\n` +
        `R2 存储桶里现有 ${dryRun.total_r2_objects} 个对象:\n` +
        `  • 正在被引用: ${dryRun.in_use} 张\n` +
        `  • 孤儿 (无产品引用): ${dryRun.orphans} 张\n\n` +
        `将永久删除 ${dryRun.orphans} 张孤儿图。此操作不可撤销。\n\n` +
        `示例孤儿 key:\n${(dryRun.sample_orphans || []).slice(0, 5).map(k => "  " + k).join("\n")}\n\n` +
        `确定继续?`
      );
      if (!yes) return;
      const { data: real } = await api.post("/admin/cleanup-r2-orphans", { confirm: true });
      toast.success(`Deleted ${real.deleted} / ${real.orphans_found} orphan photos from R2`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "R2 cleanup failed");
    }
  };

  // iter 152: Wipe & Resync — drop contracts / products / price_entries /
  // sync_history and re-pull from FileMaker. Photos in R2 are preserved via
  // a per-serial backup that filemaker_sync consults when creating new
  // products, so no image re-download is needed.
  const handleWipeResync = async () => {
    const yes = window.confirm(
      "🚨 全量清空并重同步 (WIPE & RESYNC)\n\n" +
      "将删除:\n" +
      "  • contracts (合同表)\n" +
      "  • products (产品表)\n" +
      "  • price_entries (价格/变体表)\n" +
      "  • sync_history (同步历史)\n\n" +
      "保留:\n" +
      "  • carts / cart_items (报价单 - snapshot 让老报价仍能打开)\n" +
      "  • customers / users / settings / category_rules\n" +
      "  • Cloudflare R2 中的所有产品图片 (按 serial 自动回填)\n\n" +
      "然后立即用当前 Sync Date Window 触发一次全量拉取。\n" +
      "确定继续吗?"
    );
    if (!yes) return;
    try {
      const { data } = await api.post("/admin/reset-fm-data", { confirm: true });
      toast.success(
        `Wipe done: ${JSON.stringify(data.dropped)}. Photos backed up: ${data.photos_backed_up}. Full sync started.`
      );
      // Force the Stop button to show and re-poll immediately.
      syncGenRef.current += 1;
      clearTimeout(pollRef.current);
      setSyncState({ status: "running", message: "Wipe complete, starting fresh sync..." });
      pollRef.current = setTimeout(pollSyncStatus, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wipe & Resync failed");
    }
  };

  const updateSysSettings = (key, value) => setSysSettings((s) => ({ ...s, [key]: value }));

  const handleSaveSettings = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/settings", {
        sea_freight_per_container: sysSettings.sea_freight_per_container === "" ? null : parseFloat(sysSettings.sea_freight_per_container),
        usd_to_rmb_rate: sysSettings.usd_to_rmb_rate === "" ? null : parseFloat(sysSettings.usd_to_rmb_rate),
        usd_to_cad_rate: sysSettings.usd_to_cad_rate === "" ? null : parseFloat(sysSettings.usd_to_cad_rate),
        quotation_rate: sysSettings.quotation_rate === "" ? null : parseFloat(sysSettings.quotation_rate),
        profit_margin_rate: sysSettings.profit_margin_rate === "" ? null : parseFloat(sysSettings.profit_margin_rate),
        default_tax_rebate_rate: sysSettings.default_tax_rebate_rate === "" ? null : parseFloat(sysSettings.default_tax_rebate_rate),
        sync_date_from: sysSettings.sync_date_from,
        sync_date_to: sysSettings.sync_date_to,
        sync_tag_filter: sysSettings.sync_tag_filter,
        photo_auto_refresh_days: sysSettings.photo_auto_refresh_days === "" ? null : parseInt(sysSettings.photo_auto_refresh_days, 10),
      });
      toast.success("System settings saved");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const isSyncing = syncState?.status === "running";
  const isPhotoBackfilling = syncState?.photo_backfill?.status === "running";
  const isClassifying = syncState?.category_classification?.status === "running";

  return (
    <AppLayout>
      <div data-testid="settings-page" className="max-w-2xl space-y-10">
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">System Settings</h1>
            <Button
              type="button"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              data-testid="settings-save-button-header"
              className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors"
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </Button>
          </div>

          <form onSubmit={handleSaveSettings} className="bg-white border border-gray-200 rounded-xl p-6 space-y-5" data-testid="system-settings-form">
            <div className="flex items-center gap-2 text-gray-700 mb-2">
              <Settings2 size={16} strokeWidth={1.75} />
              <p className="text-xs font-semibold uppercase tracking-label text-gray-500">Freight & Exchange Rates (Admin)</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-600">Sea Freight (per container, USD)</Label>
              <Input
                data-testid="settings-sea-freight-input"
                type="number"
                step="0.01"
                value={sysSettings.sea_freight_per_container}
                onChange={(e) => updateSysSettings("sea_freight_per_container", e.target.value)}
                placeholder="e.g. 3500"
                className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600">USD → RMB Exchange Rate</Label>
                <Input
                  data-testid="settings-usd-rmb-rate-input"
                  type="number"
                  step="0.0001"
                  value={sysSettings.usd_to_rmb_rate}
                  onChange={(e) => updateSysSettings("usd_to_rmb_rate", e.target.value)}
                  placeholder="e.g. 7.20"
                  className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600">USD → CAD Exchange Rate</Label>
                <Input
                  data-testid="settings-usd-cad-rate-input"
                  type="number"
                  step="0.0001"
                  value={sysSettings.usd_to_cad_rate}
                  onChange={(e) => updateSysSettings("usd_to_cad_rate", e.target.value)}
                  placeholder="e.g. 1.36"
                  className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600">Quotation Rate (used for USD cart conversion)</Label>
                <Input
                  data-testid="settings-quotation-rate-input"
                  type="number"
                  step="0.0001"
                  value={sysSettings.quotation_rate}
                  onChange={(e) => updateSysSettings("quotation_rate", e.target.value)}
                  placeholder="e.g. 7.00"
                  className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600">Profit Margin (%)</Label>
                <Input
                  data-testid="settings-profit-margin-input"
                  type="number"
                  step="0.01"
                  value={sysSettings.profit_margin_rate}
                  onChange={(e) => updateSysSettings("profit_margin_rate", e.target.value)}
                  placeholder="e.g. 30"
                  className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600" title="Default 退税率 for products without an explicit value. Enter a percentage (13 = 13%), NOT a decimal fraction (0.13).">Default Tax Rebate (%)</Label>
                <Input
                  data-testid="settings-default-tax-rebate-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={sysSettings.default_tax_rebate_rate}
                  onChange={(e) => updateSysSettings("default_tax_rebate_rate", e.target.value)}
                  placeholder="e.g. 13"
                  className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Enter as percentage — e.g. 13 for 13%, not 0.13.
                </p>
              </div>
            </div>
          </form>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">FileMaker Data Sync</h1>
            <Button
              type="button"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              data-testid="settings-save-button-sync-header"
              className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors"
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </Button>
          </div>

          <div
            data-testid="filemaker-status-banner"
            className={`flex items-start gap-3 border rounded-lg px-5 py-4 ${
              fmStatus?.connected ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
            }`}
          >
            {checkingStatus ? (
              <Loader2 size={18} strokeWidth={1.75} className="text-gray-400 flex-shrink-0 mt-0.5 animate-spin" />
            ) : fmStatus?.connected ? (
              <CheckCircle2 size={18} strokeWidth={1.75} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle size={18} strokeWidth={1.75} className="text-amber-500 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={`text-sm font-bold ${fmStatus?.connected ? "text-emerald-800" : "text-amber-800"}`}>
                {checkingStatus ? "Checking connection..." : fmStatus?.connected ? "Connected" : "Not Connected"}
              </p>
              <p className={`text-sm mt-1 leading-relaxed ${fmStatus?.connected ? "text-emerald-700/80" : "text-amber-700/80"}`}>
                {fmStatus?.message || "Checking FileMaker server connection status..."}
              </p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5" data-testid="filemaker-sync-card">
            <p className="text-sm text-gray-600 leading-relaxed" data-testid="filemaker-sync-condition-description">
              Sync runs in three stages so the product list stays responsive. <span className="font-semibold text-gray-900">Stage 1 — text sync</span> imports every contract product with a Contract Date on or after January 1, 2025 (typically 15-25 minutes for a full year). Products become usable immediately with placeholders for missing photos. <span className="font-semibold text-gray-900">Stage 2 — photo backfill</span> downloads product photos in small batches with fresh FileMaker sessions per batch, running progressively in the background. <span className="font-semibold text-gray-900">Stage 3 — AI category</span> (Gemini 3.1 Pro) auto-fills the Category field for any product FileMaker didn't already tag, using its description text. All three stages auto-run after text sync; you can also re-run stages 2 and 3 manually below.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed" data-testid="filemaker-sync-schedule-note">
              An automatic sync also runs daily at 7:00 PM (Beijing Time), in addition to the manual "Sync Now" button below.
            </p>

            {/* iter 195b (2026-02): last-sync status panel. Shows the
                completion time, mode, duration, filter, and pre-sync
                mongodump backup path so the user can trace exactly what
                the most recent sync produced. */}
            {syncState?.finished_at && (
              <div className="border border-gray-100 rounded-lg p-4 bg-emerald-50/40" data-testid="last-sync-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={14} strokeWidth={1.75} className="text-emerald-700" />
                  <p className="text-xs font-semibold uppercase tracking-label text-emerald-800">Last Sync</p>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-gray-700">
                  <div><span className="text-gray-500">Finished:</span> <span className="font-mono" data-testid="last-sync-finished-at">{new Date(syncState.finished_at).toLocaleString()}</span></div>
                  <div><span className="text-gray-500">Duration:</span> <span className="font-mono" data-testid="last-sync-duration">{syncState.started_at ? `${Math.max(1, Math.round((new Date(syncState.finished_at) - new Date(syncState.started_at)) / 1000))}s` : "—"}</span></div>
                  <div><span className="text-gray-500">Mode:</span> <span className="font-mono" data-testid="last-sync-mode">{syncState.result?.sync_mode || syncState.mode || "—"}</span></div>
                  <div><span className="text-gray-500">Filter:</span> <span className="font-mono" data-testid="last-sync-filter">{syncState.result?.filter_mode || "—"}</span></div>
                  {syncState.result && (
                    <div className="col-span-2 pt-1 border-t border-emerald-100 mt-1">
                      <span className="text-gray-500">Result:</span>{" "}
                      <span className="font-mono" data-testid="last-sync-counts">
                        {syncState.result.products_created ?? 0} +新 / {syncState.result.products_updated ?? 0} 改 / {syncState.result.products_removed ?? 0} 删 · {syncState.result.price_entries_created ?? 0} +变体 / {syncState.result.price_entries_updated ?? 0} 改变体 / {syncState.result.price_entries_removed ?? 0} 删变体
                      </span>
                      {syncState.result.reconciliation_aborted && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs font-semibold" data-testid="last-sync-reconcile-aborted">⚠️ Reconciliation aborted (safety)</span>
                      )}
                    </div>
                  )}
                  {syncState.backup?.path && (
                    <div className="col-span-2 flex items-center gap-1.5">
                      <Archive size={11} strokeWidth={1.75} className="text-gray-400" />
                      <span className="text-gray-500">Backup:</span>{" "}
                      <span className="font-mono text-xs text-gray-600" data-testid="last-sync-backup-path">{syncState.backup.path}</span>
                      <span className="text-gray-400">({syncState.backup.size_kb} KB)</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50" data-testid="sync-date-window-card">
              <p className="text-xs font-semibold uppercase tracking-label text-gray-500 mb-3">Sync Date Window (MM/DD/YYYY)</p>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                Filters product_main, data_api, and contract records by their respective entry/working dates. Leave "To" empty for open-ended (from date onwards). Preview and production can use different windows independently.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-600">From (Date)</Label>
                  <DatePickerField
                    testId="settings-sync-date-from-input"
                    value={sysSettings.sync_date_from}
                    onChange={(v) => updateSysSettings("sync_date_from", v)}
                    placeholder="e.g. 01/01/2025"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-600">To (Date, optional)</Label>
                  <DatePickerField
                    testId="settings-sync-date-to-input"
                    value={sysSettings.sync_date_to}
                    onChange={(v) => updateSysSettings("sync_date_to", v)}
                    placeholder="leave blank for open-ended"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Saved via the <span className="font-medium">Save Settings</span> button at the top of this section; applies to the next sync run.
              </p>
            </div>

            {/* iter 116: Sync Tag Filter is a rarely-used advanced switch that
                restricts sync to entries carrying a specific tag. When set by
                mistake it silently drops every record (looks like "sync isn't
                running"). Expose it here with a Clear button so users can
                self-remedy without DB access. */}
            <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50" data-testid="sync-tag-filter-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-label text-gray-500">Sync Tag Filter (advanced)</p>
                {sysSettings.sync_tag_filter && (
                  <button
                    type="button"
                    onClick={() => updateSysSettings("sync_tag_filter", "")}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                    data-testid="sync-tag-filter-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2 leading-relaxed">
                Restricts sync to entries carrying this exact tag. <span className="font-semibold text-red-600">Leave blank</span> unless you know you need this — a stale/wrong value here will make every sync return zero records.
              </p>
              <Input
                data-testid="settings-sync-tag-filter-input"
                type="text"
                value={sysSettings.sync_tag_filter}
                onChange={(e) => updateSysSettings("sync_tag_filter", e.target.value)}
                placeholder="(blank = no tag filter, syncs everything)"
                className="rounded-lg border-gray-300 focus-visible:ring-brand-500 font-mono text-sm"
              />
              {sysSettings.sync_tag_filter && (
                <p className="text-xs text-amber-700 mt-2 font-medium">
                  ⚠️ Currently filtering syncs to tag: <span className="font-mono">{sysSettings.sync_tag_filter}</span>
                </p>
              )}
            </div>

            {/* iter 178c: photo auto-refresh window — detects photo updates
                you made in FM without having to manually click "Force Refresh
                Date Range". Only touches products whose FM date_enter falls
                inside the window so historical products stay untouched. */}
            <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50" data-testid="photo-auto-refresh-card">
              <p className="text-xs font-semibold uppercase tracking-label text-gray-500 mb-2">Photo Auto-Refresh Window</p>
              <p className="text-xs text-gray-500 mb-2 leading-relaxed">
                On every FileMaker sync, automatically re-download photos for products whose <span className="font-mono">date_enter</span> is within the last <span className="font-medium">N days</span>. This catches photo updates you made in FM without having to click "Force Refresh Date Range" manually. Set to <span className="font-medium">0</span> to disable, or up to 90 days.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="settings-photo-auto-refresh-input"
                  type="number"
                  min={0}
                  max={90}
                  step={1}
                  value={sysSettings.photo_auto_refresh_days}
                  onChange={(e) => updateSysSettings("photo_auto_refresh_days", e.target.value)}
                  className="rounded-lg border-gray-300 focus-visible:ring-brand-500 font-mono text-sm w-28"
                />
                <span className="text-xs text-gray-500">days</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Default 7 days. Products with a bulky historical catalog will barely see any bandwidth impact; the window covers recent activity only.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={() => handleSync("contracts")}
                disabled={isSyncing}
                data-testid="filemaker-sync-contracts-button"
                variant="outline"
                className="rounded-lg border-gray-300"
              >
                <RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> Sync Contracts
              </Button>
              <Button
                type="button"
                onClick={() => handleSync("products")}
                disabled={isSyncing}
                data-testid="filemaker-sync-products-button"
                variant="outline"
                className="rounded-lg border-gray-300"
              >
                <RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> Sync Products
              </Button>
              <Button
                type="button"
                onClick={() => handleSync("incremental")}
                disabled={isSyncing}
                data-testid="filemaker-sync-incremental-button"
                className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                title="Only re-fetch rows modified since the last sync. Typically 3–10 seconds."
              >
                {isSyncing ? (
                  <><Loader2 size={14} strokeWidth={1.75} className="mr-1.5 animate-spin" /> Syncing...</>
                ) : (
                  <><Zap size={14} strokeWidth={1.75} className="mr-1.5" /> Quick Sync</>
                )}
              </Button>
              <Button
                type="button"
                onClick={() => handleSync("all")}
                disabled={isSyncing}
                data-testid="filemaker-sync-button"
                className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors"
              >
                {isSyncing ? (
                  <><Loader2 size={14} strokeWidth={1.75} className="mr-1.5 animate-spin" /> Syncing...</>
                ) : (
                  <><RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> All Sync</>
                )}
              </Button>
              {isSyncing && (
                <Button
                  type="button"
                  onClick={handleStopSync}
                  data-testid="filemaker-sync-stop-button"
                  variant="outline"
                  className="rounded-lg border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                >
                  <XCircle size={14} strokeWidth={1.75} className="mr-1.5" /> Stop
                </Button>
              )}
              <Button
                type="button"
                onClick={handlePhotoBackfill}
                disabled={isSyncing || isPhotoBackfilling}
                data-testid="filemaker-photo-backfill-button"
                variant="outline"
                className="rounded-lg border-gray-300"
              >
                {isPhotoBackfilling ? (
                  <><Loader2 size={14} strokeWidth={1.75} className="mr-1.5 animate-spin" /> Backfilling photos...</>
                ) : (
                  <><RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> Backfill Photos</>
                )}
              </Button>
              <Button
                type="button"
                onClick={handleClassifyCategories}
                disabled={isSyncing || isClassifying}
                data-testid="filemaker-classify-categories-button"
                variant="outline"
                className="rounded-lg border-gray-300"
              >
                {isClassifying ? (
                  <><Loader2 size={14} strokeWidth={1.75} className="mr-1.5 animate-spin" /> Classifying...</>
                ) : (
                  <><RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> Classify Categories (AI)</>
                )}
              </Button>
              {/* iter 163: dedicated retry over 'unknown' rows only. Doesn't
                  touch user-overridden categories, and doesn't spend LLM
                  budget on already-classified products. */}
              <Button
                type="button"
                onClick={handleRetryUnknowns}
                disabled={isClassifying}
                data-testid="filemaker-retry-unknowns-button"
                variant="outline"
                className="rounded-lg border-gray-300"
              >
                <RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> Retry Unknowns (AI)
              </Button>
              <Button
                type="button"
                onClick={() => setManualCategorizeOpen(true)}
                data-testid="filemaker-manual-categorize-button"
                variant="outline"
                className="rounded-lg border-gray-300"
              >
                <RefreshCw size={14} strokeWidth={1.75} className="mr-1.5" /> Manual Categorize
              </Button>
              {/* iter 162: removed 4 admin-only maintenance buttons per user
                  request. Underlying endpoints remain callable via API. */}
            </div>

            {syncState && syncState.status !== "idle" && (
              <div data-testid="filemaker-sync-progress" tabIndex={-1} className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-1 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                <p className="font-medium">{syncState.message}</p>
                {syncState.triggered_by && (
                  <p className="text-xs text-gray-500" data-testid="filemaker-sync-triggered-by">
                    Triggered: {syncState.triggered_by === "scheduled" ? "Automatic daily sync" : "Manual"}
                    {syncState.finished_at ? ` · Last run: ${new Date(syncState.finished_at).toLocaleString("en-US")}` : ""}
                  </p>
                )}
                {syncState.status === "completed" && syncState.result && (
                  <ul className="text-gray-600 space-y-0.5 mt-2">
                    <li>Filter mode: {syncState.result.filter_mode}</li>
                    <li>Matching contracts: {syncState.result.matching_contracts}</li>
                    <li>Products created: {syncState.result.products_created} (updated: {syncState.result.products_updated})</li>
                    <li>Products removed (no longer matching): {syncState.result.products_removed} (archived — in use in a quotation: {syncState.result.products_archived_in_use})</li>
                    <li>Price entries created: {syncState.result.price_entries_created} (updated: {syncState.result.price_entries_updated})</li>
                    {syncState.result.photos_pending > 0 && (
                      <li className="text-brand-700 font-medium">Photos pending backfill: {syncState.result.photos_pending}</li>
                    )}
                    {syncState.result.photo_refresh_by_mod > 0 && (
                      <li className="text-amber-700 font-medium" data-testid="sync-photo-refresh-by-mod">Photos re-queued (FM record modified): {syncState.result.photo_refresh_by_mod}</li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {syncState?.photo_backfill && (syncState.photo_backfill.status !== "idle" || syncState.photo_backfill.remaining_pending > 0) && (
              <div data-testid="filemaker-photo-backfill-progress" className="text-sm text-gray-700 bg-brand-50 border border-brand-100 rounded-lg p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Photo Backfill</p>
                  <span className="text-xs uppercase tracking-wider font-mono text-brand-600">{syncState.photo_backfill.status}</span>
                </div>
                {syncState.photo_backfill.message && (
                  <p className="text-xs text-gray-600">{syncState.photo_backfill.message}</p>
                )}
                {(syncState.photo_backfill.completed_this_run + syncState.photo_backfill.remaining_pending) > 0 && (
                  <div className="pt-2">
                    <div className="h-1.5 w-full bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 transition-all"
                        style={{
                          width: `${Math.min(100, Math.round((syncState.photo_backfill.completed_this_run / Math.max(1, syncState.photo_backfill.completed_this_run + syncState.photo_backfill.remaining_pending)) * 100))}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 font-mono">
                      Downloaded {syncState.photo_backfill.completed_this_run} · Failed {syncState.photo_backfill.failed_this_run} · Remaining {syncState.photo_backfill.remaining_pending}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* iter 164: photo health breakdown - always visible so users
                see exactly what state each product's photo is in. */}
            {photoHealth && (
              <div data-testid="photo-health-panel" className="text-sm text-gray-700 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                <p className="font-medium text-slate-900">Photo Health Breakdown</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2 text-xs font-mono">
                  <div data-testid="photo-health-total">
                    <div className="text-slate-500">Total products</div>
                    <div className="text-base font-semibold text-slate-800">{photoHealth.total_products}</div>
                  </div>
                  <div data-testid="photo-health-has-photo">
                    <div className="text-slate-500">Has photo</div>
                    <div className="text-base font-semibold text-emerald-700">{photoHealth.has_photo}</div>
                  </div>
                  <div data-testid="photo-health-no-source" title="Products where FileMaker has no source image (photo_container_url is empty). User must upload the image to FM.">
                    <div className="text-slate-500">No source in FM</div>
                    <div className="text-base font-semibold text-gray-700">{photoHealth.no_source_in_fm}</div>
                  </div>
                  <div data-testid="photo-health-skipped" title="Products where the user has flagged photo backfill as 'skip' (usually after decode failures). Click Retry Skipped to reset.">
                    <div className="text-slate-500">Skipped by user</div>
                    <div className="text-base font-semibold text-amber-700">{photoHealth.skipped_by_user}</div>
                  </div>
                  <div data-testid="photo-health-pending" title="Products with a container URL but no photo yet - the next Backfill Photos run will fetch them.">
                    <div className="text-slate-500">Pending backfill</div>
                    <div className="text-base font-semibold text-blue-700">{photoHealth.pending_backfill}</div>
                  </div>
                  <div data-testid="photo-health-failed" title="Products that errored on the last backfill attempt and haven't been skipped yet.">
                    <div className="text-slate-500">Failed last run</div>
                    <div className="text-base font-semibold text-red-700">{photoHealth.failed_last_run}</div>
                  </div>
                </div>
                {photoHealth.no_source_in_fm > 0 && (
                  <div className="pt-2 border-t border-slate-200 flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={handleRefreshPhotoSources}
                      disabled={photoActionLoading}
                      data-testid="photo-refresh-sources-button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg border-slate-400 text-slate-800 hover:bg-slate-100 font-semibold"
                    >
                      Re-query FM for {photoHealth.no_source_in_fm} missing source{photoHealth.no_source_in_fm === 1 ? "" : "s"}
                    </Button>
                    <span className="text-xs text-slate-500">Ignores sync date filter · pulls whatever FM has now</span>
                  </div>
                )}
                {/* iter 166: single-product diagnostic — compares DB state vs
                    live FM lookup for a specific 序列号, then force-refresh. */}
                <div className="pt-2 border-t border-slate-200 space-y-2">
                  <p className="text-xs text-slate-600">Diagnose a single product:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={inspectSerial}
                      onChange={(e) => setInspectSerial(e.target.value)}
                      placeholder="序列号 e.g. 207860"
                      data-testid="photo-inspect-input"
                      className="flex-1 max-w-xs px-3 py-1.5 text-sm border border-slate-300 rounded-md font-mono focus:outline-none focus:border-brand-500"
                    />
                    <Button type="button" onClick={handleInspect} disabled={inspectLoading} data-testid="photo-inspect-button" variant="outline" size="sm">Inspect</Button>
                    {inspectResult?.fm?.fm_photo_url && (
                      <Button type="button" onClick={handleForceRefresh} disabled={inspectLoading} data-testid="photo-force-refresh-button" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        Force Refresh + Clear
                      </Button>
                    )}
                  </div>
                  {inspectResult && (
                    <div className="grid grid-cols-2 gap-3 text-xs font-mono bg-white rounded border border-slate-200 p-3">
                      <div>
                        <p className="font-semibold text-slate-700 mb-1">DB State</p>
                        {inspectResult.db ? (
                          <>
                            <div>item_no: {inspectResult.db.item_no}</div>
                            <div>contract: {inspectResult.db.contract_no}</div>
                            <div>photo_url: <span className={inspectResult.db.photo_url ? "text-emerald-700" : "text-red-600"}>{inspectResult.db.photo_url ? "SET" : "empty"}</span></div>
                            <div>container: <span className={inspectResult.db.photo_container_url ? "text-emerald-700" : "text-red-600"}>{inspectResult.db.photo_container_url ? "SET" : "empty"}</span></div>
                            {inspectResult.db.photo_backfill_skip && <div className="text-amber-700">SKIP flag: TRUE</div>}
                            {inspectResult.db.photo_backfill_error && <div className="text-red-700 truncate" title={inspectResult.db.photo_backfill_error}>err: {inspectResult.db.photo_backfill_error}</div>}
                          </>
                        ) : (
                          <div className="text-red-600">Not in DB</div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700 mb-1">FileMaker State (live)</p>
                        {inspectResult.fm.found_in_fm ? (
                          <>
                            <div>contract: {inspectResult.fm.fm_contract_no}</div>
                            <div>Date_enter: {inspectResult.fm.fm_date_enter}</div>
                            <div>产品图片(1): <span className={inspectResult.fm.fm_photo_url ? "text-emerald-700" : "text-red-600"}>{inspectResult.fm.fm_photo_url ? "SET" : "empty"}</span></div>
                            {inspectResult.fm.fm_photo_url && (
                              <a href={inspectResult.fm.fm_photo_url} target="_blank" rel="noreferrer" className="text-brand-600 underline text-[10px] break-all">Open FM URL</a>
                            )}
                          </>
                        ) : (
                          <div className="text-red-600">Not found in FileMaker</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* iter 167: Path B bulk fix — for products whose R2 photo_url
                    is stale (points to a wiped R2 object), scoped by
                    date_enter range. Clears photo_url + refreshes container
                    from FM so the next Backfill Photos re-downloads. */}
                <div className="pt-2 border-t border-slate-200 space-y-2">
                  <p className="text-xs text-slate-600">Batch refresh photos by date range (fixes stale R2 URLs):</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="date"
                      value={rangeFrom}
                      onChange={(e) => setRangeFrom(e.target.value)}
                      data-testid="photo-range-from"
                      className="px-2 py-1.5 text-sm border border-slate-300 rounded-md font-mono focus:outline-none focus:border-brand-500"
                    />
                    <span className="text-xs text-slate-500">to</span>
                    <input
                      type="date"
                      value={rangeTo}
                      onChange={(e) => setRangeTo(e.target.value)}
                      data-testid="photo-range-to"
                      className="px-2 py-1.5 text-sm border border-slate-300 rounded-md font-mono focus:outline-none focus:border-brand-500"
                    />
                    <label className="flex items-center gap-1.5 text-xs text-slate-700 select-none">
                      <input type="checkbox" checked={rangeDryRun} onChange={(e) => setRangeDryRun(e.target.checked)} data-testid="photo-range-dry-run" />
                      Dry run
                    </label>
                    <Button
                      type="button"
                      onClick={handleForceRefreshRange}
                      disabled={rangeLoading}
                      data-testid="photo-range-run-button"
                      size="sm"
                      className={rangeDryRun ? "" : "bg-red-600 hover:bg-red-700 text-white"}
                    >
                      {rangeLoading ? "Running…" : (rangeDryRun ? "Preview count" : "Force refresh range")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* iter 158: photo skip management. Shows when there's at least one
                failed or skipped photo so users can flag persistent failures
                and skip them until fixed in FileMaker. */}
            {(photoStats.failed_current > 0 || photoStats.skipped > 0) && (
              <div data-testid="photo-skip-manager" className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <div>
                  <p className="font-medium text-amber-900">Photo Backfill · Persistent Failures</p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Mark broken photos as skipped so future syncs don't retry them. Fix the container URL in FileMaker, then click "Retry skipped".
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono">
                  <span data-testid="photo-stat-failed">Failed last run: <b className="text-red-700">{photoStats.failed_current}</b></span>
                  <span data-testid="photo-stat-skipped">Skipped (won't retry): <b className="text-amber-700">{photoStats.skipped}</b></span>
                  <span data-testid="photo-stat-pending">Pending: <b>{photoStats.pending}</b></span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={openFailuresDialog}
                    disabled={photoStats.failed_current === 0 && photoStats.skipped === 0}
                    data-testid="photo-view-failures-button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-gray-400 text-gray-800 hover:bg-gray-100 font-semibold"
                  >
                    View failure reasons
                  </Button>
                  <Button
                    type="button"
                    onClick={handleMarkFailedAsSkip}
                    disabled={photoActionLoading || photoStats.failed_current === 0 || isPhotoBackfilling}
                    data-testid="photo-mark-failed-as-skip-button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-amber-400 text-amber-800 hover:bg-amber-100 hover:text-amber-900 font-semibold"
                  >
                    Mark {photoStats.failed_current || ""} failed as skip
                  </Button>
                  <Button
                    type="button"
                    onClick={handleClearSkip}
                    disabled={photoActionLoading || photoStats.skipped === 0 || isPhotoBackfilling}
                    data-testid="photo-clear-skip-button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-emerald-400 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900 font-semibold"
                  >
                    Retry {photoStats.skipped || ""} skipped
                  </Button>
                </div>
              </div>
            )}

            {syncState?.category_classification && syncState.category_classification.status !== "idle" && (
              <div data-testid="filemaker-category-classify-progress" className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium">AI Category Classification</p>
                  <span className="text-xs uppercase tracking-wider font-mono text-emerald-700">{syncState.category_classification.status}</span>
                </div>
                {syncState.category_classification.message && (
                  <p className="text-xs text-gray-600">{syncState.category_classification.message}</p>
                )}
                {syncState.category_classification.total > 0 && (
                  <div className="pt-2">
                    <div className="h-1.5 w-full bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{
                          width: `${Math.min(100, Math.round((syncState.category_classification.processed / Math.max(1, syncState.category_classification.total)) * 100))}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 font-mono">
                      Processed {syncState.category_classification.processed} / {syncState.category_classification.total} · Unknown {syncState.category_classification.unknown}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div data-testid="sync-history-panel" className="border-t border-gray-100 pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-label text-gray-500">Sync History</p>
                  <span className="text-xs text-gray-400">(most recent 20 runs)</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={loadSyncHistory}
                  data-testid="sync-history-refresh-button"
                  className="h-7 text-xs text-gray-500 hover:text-brand-600"
                >
                  <RefreshCw size={12} strokeWidth={1.75} className="mr-1" /> Refresh
                </Button>
              </div>
              {syncHistory.length === 0 ? (
                <p className="text-xs text-gray-400 italic" data-testid="sync-history-empty">No previous syncs recorded yet.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500">
                      <tr className="border-b border-gray-100">
                        <th className="text-left font-medium px-2 py-1.5">Started</th>
                        <th className="text-left font-medium px-2 py-1.5">Mode</th>
                        <th className="text-left font-medium px-2 py-1.5">Status</th>
                        <th className="text-right font-medium px-2 py-1.5" title="Contracts upserted (or matched, for All Sync)">Contracts</th>
                        <th className="text-right font-medium px-2 py-1.5" title="Products created + updated">Products</th>
                        <th className="text-right font-medium px-2 py-1.5" title="Price entries created + updated (data_api)">Prices</th>
                      </tr>
                    </thead>
                    <tbody data-testid="sync-history-rows">
                      {syncHistory.map((h) => {
                        const r = h.result || {};
                        const mode = r.mode || h.mode || (h.status === "completed" ? "all" : "-");
                        // Contracts: for `contracts` mode it's contracts_upserted;
                        // for `all` mode we surface matching_contracts (the count of
                        // distinct contracts that had at least one product touched).
                        const contractsCount = mode === "contracts"
                          ? r.contracts_upserted
                          : (mode === "all" ? r.matching_contracts : null);
                        // Products cell: `+created / ~updated` for all + contracts
                        // (contracts mode has products_updated but no created).
                        const productsCell = (() => {
                          if (mode === "all" && r) return `+${r.products_created || 0} / ~${r.products_updated || 0}`;
                          if (mode === "products" && r) return `~${r.products_updated || 0}`;
                          if (mode === "contracts" && r) return `~${r.products_updated || 0}`;
                          return "-";
                        })();
                        // Prices only from `all` mode (contracts / products modes
                        // don't touch data_api).
                        const pricesCell = mode === "all" && r
                          ? `+${r.price_entries_created || 0} / ~${r.price_entries_updated || 0}`
                          : "-";
                        return (
                          <tr key={h.id} className="border-b border-gray-50 hover:bg-gray-50/50" title={h.message}>
                            <td className="px-2 py-1.5 text-gray-700 font-mono">
                              {h.started_at ? new Date(h.started_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                            </td>
                            <td className="px-2 py-1.5">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${
                                mode === "all" ? "bg-blue-50 text-blue-700" :
                                mode === "contracts" ? "bg-purple-50 text-purple-700" :
                                mode === "products" ? "bg-teal-50 text-teal-700" : "bg-gray-100 text-gray-600"
                              }`}>{mode}</span>
                            </td>
                            <td className="px-2 py-1.5">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${
                                h.status === "completed" ? "bg-emerald-50 text-emerald-700" :
                                h.status === "stopped" ? "bg-amber-50 text-amber-700" :
                                h.status === "error" ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600"
                              }`}>{h.status}</span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-gray-700">
                              {contractsCount != null ? contractsCount : "-"}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-gray-700">{productsCell}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-gray-700">{pricesCell}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <FileMakerAuditPanel />
          </div>
        </div>

        <SampleWarehousesPanel />

        <div className="space-y-6" data-testid="category-rules-section">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Category Learning Rules</h1>
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5" data-testid="category-rules-card">
            <p className="text-sm text-gray-600 leading-relaxed">
              Teach the classifier: when a keyword appears in a product's name/description, auto-assign a category. Applied instantly to every current <span className="font-mono text-xs bg-gray-100 px-1.5 rounded">unknown</span> product and every future sync — always winning over the AI's guess so your rules are deterministic.
            </p>

            <div className="flex flex-wrap items-end gap-3 pb-5 border-b border-gray-100" data-testid="category-rule-add-form">
              <div>
                <Label className="text-xs font-medium text-gray-600 block mb-1.5">Keyword (case-insensitive)</Label>
                <Input
                  data-testid="category-rule-keyword-input"
                  value={newRuleKeyword}
                  onChange={(e) => setNewRuleKeyword(e.target.value)}
                  placeholder="e.g. cushion"
                  className="w-52 rounded-lg border-gray-300 h-9 text-sm focus-visible:ring-brand-500"
                  onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 block mb-1.5">Category</Label>
                <Select value={newRuleCategory} onValueChange={setNewRuleCategory}>
                  <SelectTrigger data-testid="category-rule-category-select" className="w-36 rounded-lg border-gray-300 h-9 text-sm focus:ring-brand-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {["bedding", "bath", "kitchen", "rug", "window"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="category-rule-save-button"
                onClick={handleAddRule}
                disabled={savingRule || newRuleKeyword.trim().length < 2}
                className="h-9 rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors"
              >
                {savingRule ? <><Loader2 size={14} strokeWidth={1.75} className="mr-1.5 animate-spin" /> Saving...</> : <><Sparkles size={14} strokeWidth={1.75} className="mr-1.5" /> Teach Rule</>}
              </Button>
            </div>

            {categoryRules.length === 0 ? (
              <p className="text-xs text-gray-400 py-2" data-testid="category-rules-empty">No rules yet. Add one above — for example: keyword "cushion" → category "bedding".</p>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid="category-rules-list">
                {categoryRules.map((r) => {
                  const styles = { bedding: "bg-blue-50 text-blue-700 border-blue-200", bath: "bg-cyan-50 text-cyan-700 border-cyan-200", kitchen: "bg-amber-50 text-amber-700 border-amber-200", rug: "bg-orange-50 text-orange-700 border-orange-200", window: "bg-violet-50 text-violet-700 border-violet-200", pet: "bg-emerald-50 text-emerald-700 border-emerald-200" };
                  return (
                    <li key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`category-rule-${r.keyword}`}>
                      <span className="font-mono text-sm text-gray-900 truncate max-w-[240px]" title={r.keyword}>{r.keyword}</span>
                      <span className="text-gray-300">→</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[r.category] || "bg-gray-100 text-gray-600 border-gray-200"}`}>{r.category}</span>
                      <span className="ml-auto text-xs text-gray-400 font-mono">{r.hit_count} hits</span>
                      <button
                        data-testid={`category-rule-delete-${r.keyword}`}
                        onClick={() => handleDeleteRule(r)}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete rule"
                      >
                        <Trash2 size={13} strokeWidth={1.75} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Integrations</h1>
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-between" data-testid="settings-api-tokens-card">
            <div className="flex items-center gap-3">
              <KeyRound size={18} strokeWidth={1.75} className="text-gray-400" />
              <div>
                <p className="text-sm font-semibold text-gray-900">API Tokens</p>
                <p className="text-sm text-gray-500 mt-0.5">Manage read-only tokens for third-party inventory integrations.</p>
              </div>
            </div>
            <Button asChild variant="outline" data-testid="settings-manage-api-tokens-button" className="rounded-lg border-gray-300">
              <Link to="/access-tokens">Manage</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* iter 158b: grouped view of photo failures so users know WHY things
          failed (WMF decode / R2 timeout / token expired / etc). Samples give
          real item_no to look up in FileMaker. */}
      <Dialog open={failuresOpen} onOpenChange={setFailuresOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="photo-failures-dialog">
          <DialogHeader>
            <DialogTitle>Photo Backfill · Failure Reasons</DialogTitle>
            <DialogDescription>
              Grouped by error message. Fix the container URL / upload the image in FileMaker for the sample items below, then use "Retry skipped" to re-download.
            </DialogDescription>
          </DialogHeader>
          {failuresLoading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading...</p>
          ) : failuresGroups.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No failures recorded.</p>
          ) : (
            <div className="space-y-4">
              {failuresGroups.map((g, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`failure-group-${idx}`}>
                  <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                    <p className="font-mono text-xs text-red-800 break-all pr-4">{g.error}</p>
                    <span className="font-bold text-sm text-gray-800 whitespace-nowrap">× {g.count}</span>
                  </div>
                  {g.samples && g.samples.length > 0 && (
                    <div className="px-4 py-2 text-xs">
                      <p className="text-gray-500 mb-1.5">Sample items ({g.samples.length}{g.samples.length < g.count ? ` of ${g.count}` : ""}):</p>
                      <div className="flex flex-wrap gap-1.5">
                        {g.samples.map((s, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-gray-700 font-mono">
                            {s.item_no || s.serial_number || "(no id)"}
                            {s.skipped && <span className="text-amber-600 text-[10px] uppercase">skip</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ManualCategorizeDialog
        open={manualCategorizeOpen}
        onOpenChange={setManualCategorizeOpen}
      />
    </AppLayout>
  );
}
